import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb'];

const VDNB = ({ apiBase }) => {
  const [vdnbData, setVdnbData] = useState([]);
  const [ethnicityStats, setEthnicityStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [propertyFormData, setPropertyFormData] = useState({
    property_type: '',
    property_address: '',
    property_location: '',
    property_area: '',
    property_area_unit: 'sqft',
    bhk_type: '',
    expected_price: '',
    property_age: '',
    floor_number: '',
    parking_available: false,
    amenities: '',
    reason_for_selling: '',
    preferred_brokers: '',
    notes: ''
  });

  useEffect(() => {
    fetchVDNB();
    fetchEthnicityStats();
  }, []);

  const fetchVDNB = async () => {
    try {
      const response = await axios.get(`${apiBase}/vdnb`);
      setVdnbData(response.data);
    } catch (error) {
      console.error('Error fetching VDNB data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEthnicityStats = async () => {
    try {
      const response = await axios.get(`${apiBase}/vdnb/ethnicity-stats`);
      setEthnicityStats(response.data);
    } catch (error) {
      console.error('Error fetching ethnicity stats:', error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${apiBase}/vdnb/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vdnb-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting VDNB:', error);
      alert('Error exporting data');
    }
  };

  const handleAddProperty = (client) => {
    setSelectedClient(client);
    setShowPropertyForm(true);
    setPropertyFormData({
      property_type: '',
      property_address: '',
      property_location: '',
      property_area: '',
      property_area_unit: 'sqft',
      bhk_type: '',
      expected_price: '',
      property_age: '',
      floor_number: '',
      parking_available: false,
      amenities: '',
      reason_for_selling: '',
      preferred_brokers: '',
      notes: ''
    });
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/vdnb/${selectedClient.id}/properties-to-sell`, propertyFormData);
      alert('Property details saved successfully!');
      setShowPropertyForm(false);
      fetchVDNB();
    } catch (error) {
      console.error('Error saving property details:', error);
      alert('Error saving property details');
    }
  };

  if (loading) {
    return <div className="loading">Loading VDNB data...</div>;
  }

  return (
    <div>
      {/* Ethnicity Distribution Charts */}
      {ethnicityStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <div className="card">
            <h2 className="card-title">Ethnicity Distribution (Pie Chart)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ethnicityStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {ethnicityStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h2 className="card-title">Ethnicity Distribution (Bar Chart)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ethnicityStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="ethnicity" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">VDNB (Client Database)</h2>
          <button className="btn btn-primary" onClick={handleExport}>
            Export CSV
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Address</th>
                <th>Ethnicity</th>
                <th>Selling Property</th>
                <th>Source</th>
                <th>Project</th>
                <th>Channel Partner</th>
                <th>Status</th>
                <th>Walk-ins</th>
                <th>Follow-ups</th>
                <th>Sales</th>
                <th>Actions</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {vdnbData.map((client) => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td>{client.phone}</td>
                  <td>{client.email || '-'}</td>
                  <td>{client.address || '-'}</td>
                  <td>
                    <span className="badge badge-info">
                      {client.ethnicity || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    {client.is_selling_property ? (
                      <span className="badge badge-success">Yes</span>
                    ) : (
                      <span className="badge badge-secondary">No</span>
                    )}
                  </td>
                  <td>{client.source || '-'}</td>
                  <td>{client.project_name || '-'}</td>
                  <td>{client.channel_partner_name || '-'}</td>
                  <td>
                    <span className={`badge badge-${getStatusColor(client.status)}`}>
                      {client.status}
                    </span>
                  </td>
                  <td>{client.walkin_count || 0}</td>
                  <td>{client.followup_count || 0}</td>
                  <td>{client.sale_count || 0}</td>
                  <td>
                    {!client.is_selling_property && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleAddProperty(client)}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        Add Property
                      </button>
                    )}
                  </td>
                  <td>{new Date(client.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Property Selling Form Modal */}
      {showPropertyForm && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-content" style={{ maxWidth: '600px', margin: '50px auto' }}>
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">
                  Add Property to Sell - {selectedClient?.name}
                </h2>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPropertyForm(false)}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handlePropertySubmit}>
                <div style={{ padding: '20px' }}>
                  <div className="form-group">
                    <label>Property Type *</label>
                    <select
                      className="form-control"
                      value={propertyFormData.property_type}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, property_type: e.target.value })}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Apartment">Apartment</option>
                      <option value="Villa">Villa</option>
                      <option value="Plot">Plot</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Property Address *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={propertyFormData.property_address}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, property_address: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Location *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={propertyFormData.property_location}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, property_location: e.target.value })}
                      required
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>Area *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={propertyFormData.property_area}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, property_area: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Area Unit</label>
                      <select
                        className="form-control"
                        value={propertyFormData.property_area_unit}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, property_area_unit: e.target.value })}
                      >
                        <option value="sqft">Sqft</option>
                        <option value="sqm">Sqm</option>
                        <option value="acre">Acre</option>
                        <option value="hectare">Hectare</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>BHK Type</label>
                      <select
                        className="form-control"
                        value={propertyFormData.bhk_type}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, bhk_type: e.target.value })}
                      >
                        <option value="">Select BHK</option>
                        <option value="1 BHK">1 BHK</option>
                        <option value="2 BHK">2 BHK</option>
                        <option value="3 BHK">3 BHK</option>
                        <option value="4 BHK">4 BHK</option>
                        <option value="5+ BHK">5+ BHK</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Expected Price (₹) *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={propertyFormData.expected_price}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, expected_price: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>Property Age (Years)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={propertyFormData.property_age}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, property_age: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Floor Number</label>
                      <input
                        type="number"
                        className="form-control"
                        value={propertyFormData.floor_number}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, floor_number: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={propertyFormData.parking_available}
                        onChange={(e) => setPropertyFormData({ ...propertyFormData, parking_available: e.target.checked })}
                      />
                      {' '}Parking Available
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Amenities</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={propertyFormData.amenities}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, amenities: e.target.value })}
                      placeholder="e.g., Swimming Pool, Gym, Clubhouse, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label>Reason for Selling</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={propertyFormData.reason_for_selling}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, reason_for_selling: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Preferred Brokers (comma-separated)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={propertyFormData.preferred_brokers}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, preferred_brokers: e.target.value })}
                      placeholder="Broker 1, Broker 2, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={propertyFormData.notes}
                      onChange={(e) => setPropertyFormData({ ...propertyFormData, notes: e.target.value })}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button type="submit" className="btn btn-primary">
                      Save Property Details
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowPropertyForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    'lead': 'info',
    'interested': 'warning',
    'sold': 'success',
    'lost': 'danger'
  };
  return colors[status] || 'info';
};

export default VDNB;
